/**
 * Honest signup-acquisition display helpers for Admin.
 *
 * Platform comes from stored first-touch signals, strongest first:
 * Hibu (utm_source=hibu or cam=ppc-…) → click id (gclid / fbclid / ttclid) →
 * utm_source → referrer host. No signal at all → `unknown` (never `direct`).
 */

import { isHibuSignup } from './signupSource';

export const ACQUISITION_PLATFORMS = Object.freeze([
  'hibu',
  'google',
  'meta',
  'tiktok',
  'direct',
  'other',
  'unknown',
]);

const UNKNOWN = 'unknown';

/**
 * @param {unknown} host
 * @returns {string} lowercase hostname without a trailing dot or leading www.
 */
export function normalizeReferrerHost(host) {
  if (host == null) return '';
  let hostname = String(host).trim().toLowerCase();
  if (!hostname) return '';
  try {
    if (hostname.includes('://')) hostname = new URL(hostname).hostname || '';
  } catch {
    hostname = hostname.split('/')[0].split('?')[0];
  }
  hostname = hostname.replace(/\.$/, '');
  if (hostname.startsWith('www.')) hostname = hostname.slice(4);
  return hostname;
}

function isFirstPartyHost(hostname) {
  return (
    hostname === 'fiscalflowcast.com' ||
    hostname.endsWith('.fiscalflowcast.com') ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  );
}

function isGoogleHost(hostname) {
  if (!hostname) return false;
  if (hostname === 'google.com' || hostname.endsWith('.google.com')) return true;
  if (hostname === 'googleadservices.com' || hostname.endsWith('.googleadservices.com')) return true;
  if (hostname === 'youtube.com' || hostname.endsWith('.youtube.com')) return true;
  // google.co.uk, google.com.au, google.co.jp, …
  return /^google\.[a-z]{2,}(?:\.[a-z]{2})?$/.test(hostname);
}

function isMetaHost(hostname) {
  if (!hostname) return false;
  return (
    hostname === 'facebook.com' ||
    hostname.endsWith('.facebook.com') ||
    hostname === 'fb.com' ||
    hostname.endsWith('.fb.com') ||
    hostname === 'instagram.com' ||
    hostname.endsWith('.instagram.com') ||
    hostname === 'meta.com' ||
    hostname.endsWith('.meta.com')
  );
}

function isTikTokHost(hostname) {
  if (!hostname) return false;
  return (
    hostname === 'tiktok.com' ||
    hostname.endsWith('.tiktok.com') ||
    hostname === 'tiktokv.com' ||
    hostname.endsWith('.tiktokv.com')
  );
}

/**
 * Host → platform label. Blank host is always `unknown` (not `direct`).
 *
 * @param {unknown} host
 * @returns {'google'|'meta'|'tiktok'|'direct'|'other'|'unknown'}
 */
export function platformFromReferrerHost(host) {
  const hostname = normalizeReferrerHost(host);
  if (!hostname) return UNKNOWN;
  if (isGoogleHost(hostname)) return 'google';
  if (isMetaHost(hostname)) return 'meta';
  if (isTikTokHost(hostname)) return 'tiktok';
  if (isFirstPartyHost(hostname)) return 'direct';
  return 'other';
}

const UTM_SOURCE_PLATFORMS = Object.freeze({
  google: 'google',
  adwords: 'google',
  google_ads: 'google',
  facebook: 'meta',
  fb: 'meta',
  instagram: 'meta',
  ig: 'meta',
  meta: 'meta',
  tiktok: 'tiktok',
  tiktok_ads: 'tiktok',
});

/**
 * Platform from the stored record (see readAcquisitionRecord).
 *
 * @param {object} record
 * @returns {'hibu'|'google'|'meta'|'tiktok'|'direct'|'other'|'unknown'}
 */
export function platformFromAcquisition(record = {}) {
  if (isHibuSignup(record)) return 'hibu';
  if (record.gclid) return 'google';
  if (record.ttclid) return 'tiktok';
  if (record.fbclid) return 'meta';
  const utmPlatform = UTM_SOURCE_PLATFORMS[String(record.utmSource || '').trim().toLowerCase()];
  if (utmPlatform) return utmPlatform;
  return platformFromReferrerHost(record.referrer);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function nestedSignupAcquisition(record) {
  if (record.signupAcquisition && typeof record.signupAcquisition === 'object') {
    return record.signupAcquisition;
  }
  return null;
}

/**
 * Pull fields from nested `signupAcquisition` (API !410) or a bare SignupAcquisition object.
 * @param {object|null|undefined} userOrAcquisition
 * @returns {object}
 */
export function readAcquisitionRecord(userOrAcquisition) {
  if (!userOrAcquisition || typeof userOrAcquisition !== 'object') {
    return {};
  }
  const nested = nestedSignupAcquisition(userOrAcquisition);
  const src = nested || userOrAcquisition;
  return {
    referrer: firstNonEmpty(
      src.referrer,
      src.referrerHost,
      src.referrer_host,
      userOrAcquisition.referrer,
      userOrAcquisition.referrerHost,
      userOrAcquisition.referrer_host,
    ),
    landingPage: firstNonEmpty(
      src.landingPage,
      src.landing_page,
      userOrAcquisition.landingPage,
      userOrAcquisition.landing_page,
    ),
    utmSource: firstNonEmpty(src.utmSource, src.utm_source, userOrAcquisition.utmSource),
    utmMedium: firstNonEmpty(src.utmMedium, src.utm_medium, userOrAcquisition.utmMedium),
    utmCampaign: firstNonEmpty(src.utmCampaign, src.utm_campaign, userOrAcquisition.utmCampaign),
    utmTerm: firstNonEmpty(src.utmTerm, src.utm_term, userOrAcquisition.utmTerm),
    utmContent: firstNonEmpty(src.utmContent, src.utm_content, userOrAcquisition.utmContent),
    gclid: firstNonEmpty(src.gclid, userOrAcquisition.gclid),
    fbclid: firstNonEmpty(src.fbclid, userOrAcquisition.fbclid),
    ttclid: firstNonEmpty(src.ttclid, userOrAcquisition.ttclid),
    cam: firstNonEmpty(src.cam, userOrAcquisition.cam),
    signupSource: firstNonEmpty(src.signupSource, userOrAcquisition.signupSource),
  };
}

/**
 * Display values for Admin. Missing strings render as `unknown`.
 * @param {object|null|undefined} userOrAcquisition
 * @returns {{
 *   platform: string,
 *   referrerHost: string,
 *   landingPage: string,
 *   utmSource: string,
 *   utmMedium: string,
 *   utmCampaign: string,
 *   utmTerm: string,
 *   utmContent: string,
 *   gclid: string,
 *   fbclid: string,
 *   cam: string,
 *   signupSource: string,
 * }}
 */
export function formatAcquisitionDisplay(userOrAcquisition) {
  const record = readAcquisitionRecord(userOrAcquisition);
  const referrerHost = normalizeReferrerHost(record.referrer);
  return {
    platform: platformFromAcquisition(record),
    referrerHost: referrerHost || UNKNOWN,
    landingPage: record.landingPage || UNKNOWN,
    utmSource: record.utmSource || UNKNOWN,
    utmMedium: record.utmMedium || UNKNOWN,
    utmCampaign: record.utmCampaign || UNKNOWN,
    utmTerm: record.utmTerm || UNKNOWN,
    utmContent: record.utmContent || UNKNOWN,
    gclid: record.gclid || UNKNOWN,
    fbclid: record.fbclid || UNKNOWN,
    // Older users have no cam (column added in website-api migration 186).
    cam: record.cam || UNKNOWN,
    signupSource: record.signupSource || UNKNOWN,
  };
}
